import { t } from "i18next";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import classes from "./ProductCatalog.module.css";
import ProductItem from "./ProductItem";

/**
 * Flickity-inspired horizontal carousel.
 *
 * Adapted (in spirit) from the Flickity demo
 *   https://codepen.io/desandro/pen/wByaqj
 *
 * Features:
 *   - Horizontal scroll-snap track containing one cell per product.
 *   - Prev / Next round buttons that scroll one page at a time.
 *   - Page dots tracking the current page (a "page" is a viewport's worth of cells).
 *   - Mouse drag-to-scroll on top of native touch swiping.
 *   - Buttons / dots are disabled when there's nothing to scroll.
 */
const ProductCatalog = ({ className, catalog, entityName }) => {
  // Flatten { category, products[] } -> array of product cells, keeping the
  // category name available as a per-cell label.
  const cells = (catalog || []).flatMap((category) =>
    (category.products || []).map((product) => ({
      product,
      categoryName: category.name,
    }))
  );

  const trackRef = useRef(null);
  const [pageCount, setPageCount] = useState(1);
  const [selectedPage, setSelectedPage] = useState(0);

  const computePageInfo = useCallback(() => {
    const track = trackRef.current;
    if (!track) return { count: 1, current: 0 };
    const pageWidth = Math.max(1, track.clientWidth);
    const totalScroll = track.scrollWidth - track.clientWidth;
    const count = totalScroll <= 1 ? 1 : Math.ceil(track.scrollWidth / pageWidth);
    const current = Math.round(track.scrollLeft / pageWidth);
    return { count, current: Math.min(current, count - 1) };
  }, []);

  const refreshPageInfo = useCallback(() => {
    const { count, current } = computePageInfo();
    setPageCount(count);
    setSelectedPage(current);
  }, [computePageInfo]);

  useLayoutEffect(() => {
    refreshPageInfo();
  }, [refreshPageInfo, cells.length]);

  useEffect(() => {
    const onResize = () => refreshPageInfo();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [refreshPageInfo]);

  const scrollToPage = (page) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(page, pageCount - 1));
    track.scrollTo({ left: clamped * track.clientWidth, behavior: "smooth" });
  };

  const onTrackScroll = () => {
    refreshPageInfo();
  };

  // Mouse drag-to-scroll, mirroring Flickity's "draggable" behavior on desktop.
  const dragState = useRef({ isDown: false, startX: 0, startScroll: 0, moved: false });

  const onMouseDown = (e) => {
    const track = trackRef.current;
    if (!track) return;
    dragState.current = {
      isDown: true,
      startX: e.pageX,
      startScroll: track.scrollLeft,
      moved: false,
    };
    track.classList.add(classes.dragging);
  };

  const endDrag = () => {
    const track = trackRef.current;
    if (!track) return;
    if (dragState.current.isDown) {
      dragState.current.isDown = false;
      track.classList.remove(classes.dragging);
    }
  };

  const onMouseMove = (e) => {
    const track = trackRef.current;
    if (!track || !dragState.current.isDown) return;
    const dx = e.pageX - dragState.current.startX;
    if (Math.abs(dx) > 3) dragState.current.moved = true;
    track.scrollLeft = dragState.current.startScroll - dx;
  };

  // Suppress the click that follows a drag, so cards don't toggle ingredients
  // just because the user dragged the carousel.
  const onClickCapture = (e) => {
    if (dragState.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      dragState.current.moved = false;
    }
  };

  const isFirstPage = selectedPage <= 0;
  const isLastPage = selectedPage >= pageCount - 1;

  return cells.length > 0 ? (
    <div
      className={`${classes.mainContainer} ${className || ""}`}
      id="menu_container"
      aria-label={entityName ? `${entityName} ${t("products")}` : t("products")}
    >
      <div className={classes.divider} />
      <h3 className={classes.mainHeader}>{t("products")}</h3>

      <div className={classes.carousel}>
        <button
          type="button"
          className={`${classes.navButton} ${classes.navPrev}`}
          onClick={() => scrollToPage(selectedPage - 1)}
          disabled={isFirstPage}
          aria-label="Previous products"
        >
          <span aria-hidden="true">‹</span>
        </button>

        <div
          ref={trackRef}
          className={classes.carouselTrack}
          onScroll={onTrackScroll}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onClickCapture={onClickCapture}
          role="region"
          aria-roledescription="carousel"
        >
          {cells.map(({ product, categoryName }, index) => (
            <div className={classes.carouselCell} key={product.id ?? `${categoryName}-${index}`}>
              {categoryName && <h4 className={classes.categoryHeader}>{categoryName}</h4>}
              <ProductItem product={product} />
            </div>
          ))}
        </div>

        <button
          type="button"
          className={`${classes.navButton} ${classes.navNext}`}
          onClick={() => scrollToPage(selectedPage + 1)}
          disabled={isLastPage}
          aria-label="Next products"
        >
          <span aria-hidden="true">›</span>
        </button>
      </div>

      {pageCount > 1 && (
        <ol className={classes.pageDots}>
          {Array.from({ length: pageCount }).map((_, i) => (
            <li key={i}>
              <button
                type="button"
                className={`${classes.pageDot} ${
                  i === selectedPage ? classes.pageDotSelected : ""
                }`}
                onClick={() => scrollToPage(i)}
                aria-label={`Go to page ${i + 1}`}
                aria-current={i === selectedPage ? "true" : undefined}
              />
            </li>
          ))}
        </ol>
      )}
    </div>
  ) : (
    <></>
  );
};

export default ProductCatalog;
